<template>
  <div class="overflow-y-scroll h-full min-h-full pb-32">
    <div v-if="subView === 'Default'">
      <div class="flex-1 relative pb-8 z-0">
        <div class="bg-white shadow">
          <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl">
            <div class="pb-6 pt-6 md:flex md:items-center md:justify-between lg:border-t lg:border-cool-gray-200">
              <div class="text-3xl font-bold leading-7 text-cool-gray-900 sm:leading-9 sm:truncate">
                Tee Sheet
              </div>

              <div class="w-72">
                <Dropdown
                  :list="dropdownOptions"
                  list-type-name="course"
                  :default-item="selectedCourse"
                  @select="selectCourse"
                />
              </div>
            </div>

            <div class="pb-4 flex items-baseline justify-between">
              <div class="flex items-center">
                <Tabs
                  :tabs="tabs"
                  :selected-tab="selectedTab"
                  @select="setDate"
                />

                <div
                  class="cursor-pointer ml-2 text-gray-500 hover:text-gray-700 transition-all duration-500"
                  @click="showDatePicker = !showDatePicker"
                >
                  <i class="far fa-chevron-down" />
                </div>
              </div>

              <div class="flex space-x-2">
                <OverrideButton
                  :disabled="false"
                  :tee-config-id="teeConfig === undefined ? '' : teeConfig.id"
                  :selected-date="selectedDate"
                  :no-slots="slots === null || noSlots"
                  @complete="silentRefresh"
                />

                <SecondayButton
                  text="Clear All"
                  :disabled="bookings.length === 0"
                  :loading="cancelAllLoading"
                  @click="cancelAllBookings"
                />

                <SecondayButton
                  text="Block All"
                  :disabled="slots === null || noSlots"
                  :loading="blockAllLoading"
                  @click="blockAllSlots"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="px-4 pt-8 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl"
        :class="teeTimeToBook !== null?'pb-64 mb-12':''"
      >
        <Slots
          v-if="slots !== null"
          :slots="slots"
          :config="teeConfig"
          :tee-time-to-book="teeTimeToBook"
          @book="bookSlot"
        />

        <div
          v-else
          class="ml-auto mr-auto text-center"
        >
          <div
            v-if="selectedCourse === null || selectedCourse === undefined"
            class="mt-20 mb-16 max-w-sm ml-auto mr-auto"
          >
            <div
              style="font-size: 6rem"
              class="text-gray-800 mb-4"
            >
              <i class="fad fa-flag" />
            </div>
            <div class="text-lg text-gray-800 font-semibold">
              Sorry, we couldn't find a course
            </div>
            <div class="text-sm text-gray-500 mt-2 ml-auto mr-auto">
              Before we're able to configure and use a tee sheet, we'll need to know a few things about your course first.
            </div>
            <div class="w-full min-w-full flex justify-center mt-6">
              <PrimaryButton
                text="New Course"
                :width-full="true"
                @click="newCourse()"
              />
            </div>
          </div>
          <div
            v-else
            class="mt-56 mb-32"
          >
            <LoadingSpinner />
          </div>
        </div>

        <div
          v-if="noSlots"
          class="mt-20 mb-16 max-w-sm ml-auto mr-auto text-center"
        >
          <div
            style="font-size: 6rem"
            class="text-gray-800 mb-4"
          >
            <i class="fad fa-calendar-times" />
          </div>
          <div class="text-lg text-gray-800 font-semibold">
            No slots found for this date
          </div>
          <div class="text-sm text-gray-500 mt-2 ml-auto mr-auto">
            The tee sheet for {{ selectedCourse.name }} isn't configured to have any available tee times on this date.
          </div>
        </div>
      </div>
    </div>

    <div v-if="subView === 'NewConfig'">
      <div class="bg-white shadow">
        <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl">
          <div class="pb-6 pt-6 md:flex md:items-center md:justify-between lg:border-t lg:border-cool-gray-200">
            <div class="text-3xl font-bold leading-7 text-cool-gray-900 sm:leading-9 sm:truncate">
              Tee Sheet
            </div>

            <div class="w-72">
              <Dropdown
                :list="dropdownOptions"
                list-type-name="course"
                :default-item="selectedCourse"
                @select="selectCourse"
              />
            </div>
          </div>
        </div>
      </div>

      <TeeConfigForm
        @success="newConfigSuccess"
      />
    </div>

    <BookingPanel
      v-if="teeTimeToBook !== null"
      :tee-time-to-book="teeTimeToBook"
      :config="teeConfig"
      @close="teeTimeToBook = null"
      @success="bookingSuccess"
      @silentRefresh="silentRefresh"
      @removePlayer="removePlayer"
    />

    <DatePicker
      v-if="showDatePicker"
      :date="selectedDate"
      class="fixed z-20"
      style="top: 25%; right: 45.5%"
      @dateChanged="dateChanged"
    />

    <div
      v-if="showDatePicker"
      class="z-10 fixed top-0 left-0 w-full min-w-full h-full min-h-full"
      @click="showDatePicker = false"
    />
  </div>
</template>

<script>
import moment from 'moment/moment';
import { mapState } from 'vuex';
import router from '@/app/app-routes';
import Tabs from '@/app/shared/components/navigation/tabs/tabs.vue';
import Dropdown from '@/app/shared/components/form/dropdown/dropdown.vue';
import TeeConfigForm from '@/app/shared/components/form/teeConfigForm.vue';
import OverrideButton from '@/app/shared/components/teeConfig/configOverrideButton.vue';
import Form from '@/app/shared/components/form/form.vue';
import DatePicker from '@/app/shared/components/date/datePicker.vue';
import Slots from '@/app/shared/components/slots/slots.vue';
import BookingPanel from '@/app/shared/components/panels/booking/bookingPanel.vue';
import LoadingSpinner from '@/app/shared/components/base/loading/loadingSpinner.vue';
import PrimaryButton from '@/app/shared/components/base/button/primaryButton.vue';
import SecondayButton from '@/app/shared/components/base/button/secondaryButton.vue';

import { GetCoursesByClub } from '@/app/shared/services/courseService/courseService';
import { GetAllCourseConfigs, GetCourseConfig, GetCourseConfigById } from '@/app/shared/services/courseConfigService/courseConfigService';
import { ListAvailableSlots } from '@/app/shared/services/slotService/slotService';
import { CancelBooking, CreateBooking } from '@/app/shared/services/bookingService/bookingService';
import { TabItem } from '@/app/shared/components/navigation/tabs/classes';
import { GetTeeConfigByCourseId } from '@/app/shared/services/teeConfigService/teeConfigService';
import { Field, DependantField, ServiceFormModel } from '@/app/shared/components/form/classes';

export default {
  name: 'Teesheets',

  components: {
    Tabs,
    Dropdown,
    DatePicker,
    Slots,
    BookingPanel,
    TeeConfigForm,
    LoadingSpinner,
    PrimaryButton,
    SecondayButton,
    OverrideButton,
  },

  data() {
    return {
      selectedDate: new Date(),
      currentDate: new Date(),
      showDatePicker: false,

      selectedTab: 'Today',
      subView: 'Default',
      openModifyTimes: false,

      teeTimeToBook: null,
      dayIndex: 0,
      configurations: [],
      slots: null,
      teeConfig: undefined,
      newConfigForm: [],

      tabs: [],

      cancelAllLoading: false,
      blockAllLoading: false,
    };
  },

  watch: {
    courses() {
      this.slots = [];
      this.initTeeSheet();
      this.getConfig();
    },

    selectedDate() {
      this.getAvailableSlots();
    },
  },

  mounted() {
    this.getConfig();
    this.initTeeSheet();
  },

  computed: {
    noSlots() {
      return this.selectedCourse !== null && this.selectedCourse !== undefined && this.slots !== null && this.slots.length === 0;
    },

    bookings() {
      return this.slots === null ? [] : this.slots.reduce((acc, current) => acc.concat(current.bookings), []);
    },

    ...mapState({
      selectedClub: state => state.club.selectedClub,
      courses: state => state.course.availableCourses,
      selectedCourse: state => state.course.selectedCourse,
    }),

    dropdownOptions() {
      const options = [];

      if (this.courses !== [] && this.configurations !== []) {
        this.courses.forEach((element) => {
          const config = this.configurations.filter(c => c.courseId === element.id);
          if (config.length > 0) {
            const first = config[0];
            options.push({ id: element.id, primaryText: element.name, secondaryText: `${first.numberOfHoles} Holes` });
          }
        });
      }

      options.sort((a, b) => a.primaryText.localeCompare(b.primaryText));

      return options;
    },
  },

  created() {
    this.setTabs();
  },

  methods: {
    async blockAllSlots() {
      if (!this.blockAllLoading) {
        this.blockAllLoading = true;

        let cancelBookings = false;
        if (this.bookings.length > 0) {
        // eslint-disable-next-line no-alert, no-restricted-globals
          cancelBookings = confirm(`There are already ${this.bookings.length} booking(s). Would you like to cancel these bookings? Select 'Cancel' for No.`);
        }

        // eslint-disable-next-line no-restricted-globals
        const continueBlock = confirm(cancelBookings
          ? `Are you sure you want to cancel all ${this.bookings.length} bookings and block all slots?`
          : 'Are you sure you want to block all slots? Existing bookings won\'t be deleted.');

        if (continueBlock) {
          if (cancelBookings) {
            await this.cancelAllBookings(true);
          }

          await this.getAvailableSlots(true);

          const slots = this.slots.map(slot => ({
            time: slot.time,
            availability: slot.availability,
          }));

          await Promise.all(slots.map(async (b) => {
            const players = [];

            // eslint-disable-next-line no-plusplus
            for (let i = 1; i <= b.availability; i++) {
              players.push({ name: 'System', type: 6 });
            }

            try {
              await CreateBooking(this.selectedCourse.id, b.time, players);
            // eslint-disable-next-line no-empty
            } catch { }
          }));

          this.getAvailableSlots();
        }
        this.blockAllLoading = false;
      }
    },

    async cancelAllBookings(silent = false) {
      if (!this.cancelAllLoading) {
        if (!silent) {
          this.cancelAllLoading = true;
        }
        let cancelBookings = !!silent;

        if (!silent) {
        // eslint-disable-next-line no-restricted-globals
          cancelBookings = confirm('Are you sure you want to clear the tee sheet?');
        }

        if (cancelBookings) {
          await Promise.all(this.bookings.map(b => CancelBooking(b.id)));

          if (!silent) {
            this.getAvailableSlots();
          }
        }

        this.cancelAllLoading = false;
      }
    },

    setTabs() {
      const tabs = [];
      const date0 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
      const date1 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
      const date2 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
      const date3 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
      const date4 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
      const date5 = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());

      date0.setDate(date0.getDate() - 3);
      tabs.push(new TabItem(this.formatTabDate(date0), date0.toISOString()));

      date1.setDate(date1.getDate() - 2);
      tabs.push(new TabItem(this.formatTabDate(date1), date1.toISOString()));

      date2.setDate(date2.getDate() - 1);
      tabs.push(new TabItem(this.formatTabDate(date2), date2.toISOString()));

      tabs.push(new TabItem(this.formatTabDate(this.selectedDate), this.selectedDate.toISOString()));

      date3.setDate(date3.getDate() + 1);
      tabs.push(new TabItem(this.formatTabDate(date3), date3.toISOString()));

      date4.setDate(date4.getDate() + 2);
      tabs.push(new TabItem(this.formatTabDate(date4), date4.toISOString()));

      date5.setDate(date5.getDate() + 3);
      tabs.push(new TabItem(this.formatTabDate(date5), date5.toISOString()));

      this.tabs = tabs;
    },

    newCourse() {
      router.push('Courses?NewCourse=true');
    },

    newConfigSuccess() {
      this.initTeeSheet();
      this.subView = 'Default';
    },

    removePlayer(playerId) {
      this.teeTimeToBook.teeTime.availability += 1;

      for (let i = 0; i < this.teeTimeToBook.teeTime.bookings.length; i++) {
        const numOfPlayers = this.teeTimeToBook.teeTime.bookings[i].players.length;
        for (let y = 0; y < numOfPlayers; y++) {
          if (this.teeTimeToBook.teeTime.bookings[i].players[y].id === playerId) {
            if (numOfPlayers === 1) {
              // Remove booking
              this.teeTimeToBook.teeTime.bookings.splice(i, 1);
            } else {
              // Remove player
              this.teeTimeToBook.teeTime.bookings[i].players.splice(y, 1);
            }
          }
        }
      }
    },

    initForms() {
      this.newConfigForm = [];
      this.newConfigForm.push(
        new ServiceFormModel(
          0,
          0,
          'Tee sheet configuration',
          `We need to know how you would like to configure the tee sheet for ${this.selectedCourse.name}.`,
          'TeeConfiguration',
          'Create',
          [new Field('CourseId', this.selectedCourse.id), new Field('StartDate', '2020-05-01 12:00:00.0000000'), new Field('EndDate', '2021-05-01 12:00:00.0000000')],
          [],
        ),
      );
    },

    bookingSuccess() {
      this.teeTimeToBook = null;
      this.getAvailableSlots();
    },

    silentRefresh() {
      this.teeTimeToBook = null;
      ListAvailableSlots(this.selectedDate, this.selectedCourse.id).then((data) => {
        this.slots = data;
      });
    },

    initTeeSheet() {
      this.teeConfig = undefined;
      if (this.selectedCourse !== undefined && this.selectedCourse !== null) {
        GetTeeConfigByCourseId(this.selectedCourse.id).then((data) => {
          if (data.length === 0) {
            this.initForms();
            this.subView = 'NewConfig';
          } else {
            this.subView = 'Default';
            this.teeConfig = data[0];
            this.getAvailableSlots();
          }
        });
      }
    },

    bookSlot(model) {
      this.teeTimeToBook = model;
    },

    formatTabDate(date) {
      return this.currentDate.getDate() === date.getDate() ? 'Today' : moment(date).format('ddd Do');
    },

    setDate(tab) {
      this.selectedTab = tab.primaryText;
      this.selectedDate = new Date(tab.meta);
      this.setTabs();
    },

    async getAvailableSlots(silent = false) {
      if (!silent) {
        this.slots = null;
      }

      const { selectedDate } = this;
      await ListAvailableSlots(selectedDate, this.selectedCourse.id).then((data) => {
        if (this.selectedDate.toISOString() === selectedDate.toISOString()) {
          this.slots = data;
        }
      });
    },

    getConfig() {
      this.configurations = [];
      this.courses.forEach((course) => {
        GetCourseConfigById(course.id).then((data) => {
          this.configurations.push(data);
        });
      });
    },

    dateChanged(changedDate) {
      if (changedDate.toISOString() !== this.selectedDate.toISOString()) {
        this.selectedDate = changedDate;
        this.selectedTab = this.formatTabDate(changedDate);
        this.showDatePicker = false;
        this.setTabs();
      }
    },

    selectCourse(course) {
      this.teeTimeToBook = null;
      const selectedCourse = this.courses.filter(c => c.id === course[0].id);
      this.$store.dispatch('setSelectedCourse', selectedCourse[0]);
      this.initTeeSheet();
    },
  },

};
</script>
